import {
  Component,
  OnInit,
  ElementRef,
  ViewChild,
  Output,
  EventEmitter,
  Inject,
  inject
} from '@angular/core';
import moment from 'moment';
import { NgbModal, NgbTimeStruct } from '@ng-bootstrap/ng-bootstrap';
import {
  UntypedFormGroup,
  UntypedFormBuilder,
  UntypedFormControl,
  Validators
} from '@angular/forms';
import { first, catchError, map, tap } from 'rxjs/operators';
import { forkJoin, Observable, of, Subject } from 'rxjs';
import { ActivatedRoute } from '@angular/router';
import * as _ from 'underscore';
import { ThemeOption } from 'ngx-echarts';
import { BaseComponent } from '../../../components/base/base.component';
import { FreshdeskUser, Office } from '../../../models/locations';
import {
  PayrollTaskSearchConfig,
  SubTaskCompletion,
  Task,
  TaskListMode,
  TaskSearchConfig,
  TaskStatuses,
  TaskViewMode
} from '../../../models/task';
import { AdminService } from '../../../services/admin.service';
import { CompanySubTask, CompanySubTaskGroup } from '../../../models/company-subtask';
import { ApiResponse } from '../../../models/api-response';
import { CompaniesService } from '../../../services/companies.service';
import { AuthService } from '../../../services/auth.service';
import { ModalService } from '../../../services/modal.service';
import { IdNamePair } from '../../../models/id-name-pair';
import { Client, ClientNote } from '../../../models/client';
import { AuditService } from '../../../services/audit.service';
import { TaskService } from '../../../services/task.service';
import { UserNotification } from '../../../models/user-notification';
import { NotificationService } from '../../../services/notification.service';
import {
  formatDatePickerValue,
  parseDatePickerValue
} from '../../../utils/datepicker-value-parser';
import { ModalStates } from '../../../models/modal';
import { PagedPayload } from '../../../models/paged-payload';
import { AclService } from '../../../services/acl.service';
import { whitespaceValidator } from '../../../utils/whitespace-validators';
import { getObjectDeltas } from '../../../utils/object-deltas';
import { Store } from '@ngxs/store';
import { ClearClientState } from '../../../state/ClientSelectionState/ClientSelection.state';
import { DistinctBy } from '../../../utils/math';
import { ToastService } from '../../../services/toast.service';
import { AppConfigService } from '../../../services/app-config.service';

@Component({
  selector: 'app-home-page',
  templateUrl: './home-page.component.html',
  styleUrls: ['./home-page.component.css'],
  standalone: false
})
export class HomePageComponent extends BaseComponent implements OnInit {
  // auth service for role checks
  public auth: AuthService;

  // client search
  public clients: Client[] = [];
  public clientSearchObs: Observable<PagedPayload<Client>>;
  public initialClientQuery = '';

  // when tasks are edited in batch, the parent will clear all selected tasks
  public clearSelTasksSubject: Subject<void> = new Subject<void>();
  public companySubTasks: CompanySubTask[];
  public csrOfficeMap: Office[];
  public csrSearchResults: FreshdeskUser[] = [];
  public currentUserContactId: string;
  public dayStamp: string;
  public disableTaskDueDate: boolean;
  public dueByTime: NgbTimeStruct = { hour: 0, minute: 0, second: 0 };
  public echartsInstance: any;
  public expandedTasks = {};
  public hasWarnedAboutOldTasks = false;
  public invalidSelClient = false;
  public invalidTaskDueDate = false;
  public formattedTaskMinDate: string;
  public selOffice;
  public salutation: string;
  public taskLastViewedMap: Record<number, Date>;
  public taskContainer: ElementRef;

  // current task properties
  public currentTask: Task;
  public selClientCode: string;
  public selClientName: string;
  public selClientNote: ClientNote;
  public taskForm: UntypedFormGroup;
  public taskMinDate = null;
  public taskNoteLastUpdateBy: string;
  public taskNoteLastUpdateDate: string;
  public taskNoteLastUpdateTime: string;

  // current task list properties
  public currentTaskList: TaskListMode;
  public otherTasks: Task[] = [];
  public payrollTasks: Task[] = [];

  // the search configs hold all the properties needed to fetch each task list.
  // they are updated by the child components, but maintained here
  // incase either list needs to be refreshed from the parent
  public otherTaskSearchConfig: TaskSearchConfig;
  public payrollTaskSearchConfig: PayrollTaskSearchConfig;

  public selTasks = {};
  public taskSortKey: string;
  public taskSortDirection: string;
  public taskView: TaskViewMode;
  public taskViewName: string;
  public taskTypes: IdNamePair[] = [];
  public csrSearchText;

  public taskCount = {
    payroll: { open: 0, closed: 0, remaining: 0 },
    other: { open: 0, closed: 0, remaining: 0 }
  };

  // list of CSRs depending on task view selected
  // e.g. 'My Office Tasks' = CSRs from user's office
  // this list is used to populate the CSR Assignee filter dropdown
  public viewMembers: any[] = [];

  // all CSRs from any office
  public teamMembers: any[] = [];

  // doughnut chart
  theme: string | ThemeOption;
  options: any = {
    color: ['#6aaa37', '#c2c7cd'],
    title: {
      text: `${this.taskCount.payroll.closed} Closed`,
      textStyle: {
        width: '85',
        fontSize: '24',
        overflow: 'break',
        fontWeight: 'normal'
      },
      left: 'center',
      top: 'center'
    },
    series: [
      {
        type: 'pie',
        data: [
          { value: this.taskCount.payroll.closed },
          { value: this.taskCount.payroll.open + this.taskCount.payroll.remaining }
        ],
        radius: ['70%', '90%'],
        labelLine: {
          show: false
        }
      }
    ]
  };

  constructor(
    private aclService: AclService,
    private adminService: AdminService,
    private auditService: AuditService,
    private authService: AuthService,
    private companyService: CompaniesService,
    private formBuilder: UntypedFormBuilder,
    private modalService: ModalService,
    private notificationService: NotificationService,
    private ngModalService: NgbModal,
    private taskService: TaskService,
    private appConfigService: AppConfigService,
    @Inject('env') public env: any,
    private store: Store
  ) {
    super();
    this.auth = authService;
    this.saveClientNote = _.debounce(this.saveClientNote, 500);
  }
  private readonly toastService = inject(ToastService);

  get t() {
    return this.taskForm.controls;
  }
  get taskStatuses() {
    return TaskStatuses;
  }
  get taskViewMode() {
    return TaskViewMode;
  }

  ngOnInit() {
    this.store.dispatch(new ClearClientState());
    this.otherTaskSearchConfig = new TaskSearchConfig();
    this.payrollTaskSearchConfig = new PayrollTaskSearchConfig();

    this.taskForm = this.formBuilder.group({
      ClientCode: new UntypedFormControl(),
      Type: new UntypedFormControl('', Validators.required),
      CreatorAgileId: new UntypedFormControl(''),
      AssigneeAgileId: new UntypedFormControl(''),
      Priority: new UntypedFormControl('', Validators.required),
      Status: new UntypedFormControl(''),
      DueDate: new UntypedFormControl(''),
      Description: new UntypedFormControl('', [Validators.required, whitespaceValidator]),
      TaskNote: new UntypedFormControl(''),
      ClientNote: new UntypedFormControl('')
    });

    const hr = new Date().getHours();

    if (hr >= 0 && hr <= 11) {
      this.salutation = 'Good morning';
    } else if (hr >= 12 && hr <= 16) {
      this.salutation = 'Good afternoon';
    } else if (hr >= 17 && hr <= 23) {
      this.salutation = 'Good evening';
    }

    this.salutation += ' ' + this.getFirstName() + '!';

    // display day as 'Monday, January 1'
    this.dayStamp = `${moment().format('dddd')}, ${moment().format('MMMM')} ${moment().format(
      'D'
    )}`;

    this.auth.isLoggedIn().subscribe(resp => {
      if (resp) {
        this._setupTasks();
      } else {
        this.authService.isLoginComplete$.subscribe(val => {
          if (val) {
            this._setupTasks();
          }
        });
      }
    });

    this.clientSearchObs = this.companyService.searchCompanies({ query: '' });
  }

  // #region public methods

  public addTask(content) {
    this.invalidSelClient = false;
    // set required fields
    this.taskForm.controls.DueDate.setValidators(Validators.required);
    this.taskForm.controls.AssigneeAgileId.setValidators(Validators.required);
    this.taskForm.controls.Status.setValidators(Validators.required);

    this.currentTask = null;
    this.onNotesNavChange(null);

    this.invalidTaskDueDate = false;
    this.taskMinDate = null;
    const t = (this.env.defaultTaskDueTime || '').split(':');
    this.dueByTime = {
      hour: t[0] ? parseInt(t[0], 10) : 0,
      minute: t[1] ? parseInt(t[1], 10) : 0,
      second: 0
    };

    this.disableTaskDueDate = false;
    this.initialClientQuery = '';
    this.selClientCode = '';
    this.selClientName = '';
    this.t.Status.setValue(TaskStatuses.Open); // restrict to 'open'
    this.t.Status.disable();
    this.taskStatusChanged();
    this.ngModalService
      .open(content, {
        size: 'lg',
        backdrop: 'static',
        keyboard: false,
        windowClass: 'centered'
      })
      .result.then(
        result => {
          // success
          const task = new Task();
          task.ClientCode = this.selClientCode;
          task.ClientName = this.selClientName;
          task.Description = this.t.Description.value;
          task.Name = this.authService.getCurrentUserName();
          task.Subject = this.t.Type.value;
          task.Status = this.t.Status.value;
          task.Priority = this.t.Priority.value;
          task.Type = this.t.Type.value;
          task.TaskNote = this.t.TaskNote.value;
          task.AssigneeAgileId = this.t.AssigneeAgileId.value;
          task.CreatorAgileId = this.authService.getCurrentUserName();

          // concatenate date and time
          const date = moment(parseDatePickerValue(this.t.DueDate.value));
          date.set('hour', this.dueByTime ? this.dueByTime.hour : 0);
          date.set('minute', this.dueByTime ? this.dueByTime.minute : 0);
          task.DueDate = date.toDate();

          this.taskService.createTask(task).subscribe(id => {
            this.toastService.show('Save successful');
            task.Id = id;
            // update the in memory collection of tasks and counts
            // when a task is added or updatead
            this._addToTaskList(task);
            this._addToTaskCount(task);

            // audit
            const deltas = getObjectDeltas(null, task, ['IntegrityHash']);
            this.auditService.auditTask(
              `apollo-task-created`,
              {
                ClientCode: task.ClientCode,
                Id: task.Id
              },
              {
                ...deltas
              }
            );

            // ensure this new task is marked as 'read' by current user
            this.taskService.setLastTicketView(id).subscribe(() => {});
          });

          this.t.Status.enable();
          this.taskForm.reset();
        },
        () => {
          // dismiss
          this.t.Status.enable();
          this.taskForm.reset();
        }
      );
  }

  public batchEditTasks(content) {
    this.t.Status.enable();
    // remove require validation from fields when batch editing tasks
    this.taskForm.controls.DueDate.setValidators(null);
    this.taskForm.controls.AssigneeAgileId.setValidators(null);
    this.taskForm.controls.Status.setValidators(null);

    this.currentTask = null;
    this.invalidTaskDueDate = false;
    this.initialClientQuery = '';

    const time = (this.env.defaultTaskDueTime || '').split(':');
    this.dueByTime = {
      hour: time[0] ? parseInt(time[0], 10) : 0,
      minute: time[1] ? parseInt(time[1], 10) : 0,
      second: 0
    };

    let _min = null;
    this.taskMinDate = null;
    // set min date to the latest task creation date
    // to avoid freshdesk validation error
    Object.keys(this.selTasks).forEach(key => {
      const task = this.selTasks[key];
      if (_min == null || moment(task.CreatedOn).isAfter(moment(_min), 'minutes')) {
        const d = new Date(task.CreatedOn);
        _min = d;
        this.taskMinDate = {
          day: d.getDate(),
          month: d.getMonth() + 1,
          year: d.getFullYear(),
          hour: d.getHours(),
          minute: d.getMinutes()
        };
      }
    });

    this.taskStatusChanged();

    this.ngModalService
      .open(content, {
        size: 'md' as 'sm',
        backdrop: 'static',
        keyboard: false,
        windowClass: 'centered'
      })
      .result.then(
        result => {
          // success
          this._batchUpdateTasks();
          this.taskForm.reset();
        },
        () => {
          // dismiss
          this.taskForm.reset();
        }
      );
  }

  // view tasks by current user, by specific csr, by office...
  public changeTaskView(taskView: TaskViewMode, office?: Office, csr?: FreshdeskUser) {
    this.taskView = taskView;
    let assignees = [];

    if (!office) {
      // if an office has been previously selected in the task view
      // clear it here so the user is able to reselect another one
      // if they choose to go back to that view. this fixes the issue
      // where the ngModelChanges event doesn't fire.
      this.selOffice = null;
    }

    if (taskView == TaskViewMode.MyTasks) {
      const csrs = [].concat(...this.csrOfficeMap.map(x => x.CSRs));
      const csr = csrs.find(x => x.userName == this.currentUserContactId);

      if (csr) {
        this.viewMembers = [csr];
        this.taskViewName = `${csr.firstName} ${csr.lastName}`;
      }

      assignees = [this.currentUserContactId];
    } else if (taskView == TaskViewMode.MyOfficeTasks) {
      // get all CSRs in current user's office
      const o = this.csrOfficeMap.find(o =>
        o.CSRs.find(c => c.userName == `${this.currentUserContactId}`)
      );

      if (!o) {
        this.modalService.showAlertModal(
          'Error. User was not found to be a part of any office.',
          ModalStates.Error
        );
        return;
      } else {
        // set filter.assignee to list of user names
        this.viewMembers = o.CSRs;
        assignees = this.viewMembers.map(x => x.userName);
        this.taskViewName = o.Name;
      }
    } else if (taskView == TaskViewMode.OfficeTasks && office) {
      // set filter.assignee to list of freshdesk user ids
      this.viewMembers = office.CSRs.filter(x => x.userName);
      assignees = this.viewMembers.map(x => x.userName);
      this.taskViewName = office.Name;
    } else if (taskView == TaskViewMode.CSRTasks && csr) {
      this.viewMembers = [csr];
      assignees = [csr.userName];
      this.taskViewName = `${csr.firstName} ${csr.lastName}`;
    } else {
      // office tasks or csr tasks section was expanded,
      // but selection inside has not been made yet
      return;
    }

    this.otherTaskSearchConfig.Assignees = this.payrollTaskSearchConfig.Assignees = assignees;

    this.getAllTasks();
  }

  public checkDueDateTime() {
    // when creating tasks, time/date must be in the future
    // when editing tasks, time/date cannot come before the task creation date
    if (this.taskMinDate === null) {
      // user is creating a task. set minDate to now +5 minutes
      const d = moment(new Date()).add('5', 'minutes').toDate();
      this.taskMinDate = {
        ...formatDatePickerValue(d),
        hour: d.getHours(),
        minute: d.getMinutes()
      };
    }

    if (this.taskMinDate && this.t.DueDate.value) {
      const min = parseDatePickerValue(this.taskMinDate);
      const d = parseDatePickerValue(this.t.DueDate.value);

      if (moment(d).isBefore(min)) {
        this.invalidTaskDueDate = true;
      } else if (moment(d).isSame(min, 'day')) {
        if (this.dueByTime == null) {
          this.invalidTaskDueDate = true;
        } else {
          let time = moment(`${this.taskMinDate.hour}:${this.taskMinDate.minute}`, 'h:mm');
          const t = moment(`${this.dueByTime.hour}:${this.dueByTime.minute}`, 'h:mm');
          if (t.isSameOrBefore(time)) {
            this.invalidTaskDueDate = true;
          } else {
            this.invalidTaskDueDate = false;
          }
        }
      } else {
        this.invalidTaskDueDate = false;
      }
    } else {
      this.invalidTaskDueDate = false;
    }

    // if we have a taskMinDate, format it for display
    if (this.taskMinDate) {
      const t = this.taskMinDate;
      const date = new Date(t.year, t.month - 1, t.day, t.hour, t.minute);
      this.formattedTaskMinDate = moment(date).format('L h:mm a');
    } else {
      this.formattedTaskMinDate = '';
    }
  }

  public checkPastDueTasks() {
    // check all task lists for past due items
    let tasks = this.payrollTasks.concat(this.otherTasks);

    const warn = tasks.filter(
      x =>
        moment(x.DueDate).isBefore(moment(new Date())) &&
        x.Status !== TaskStatuses.Resolved &&
        x.Status !== TaskStatuses.Closed &&
        x.AssigneeAgileId == this.currentUserContactId
    );

    if (warn && warn.length > 0) {
      const msg = `You have ${warn.length > 1 ? '' : 'a '}past due task${
        warn.length > 1 ? 's' : ''
      } assigned to you. Please view the task list on the dashboard and address ${
        warn.length > 1 ? 'these items' : 'this item'
      }.`;

      this.modalService.showAlertModal(msg, ModalStates.Warning, null, null, 'Not Now');
    }
  }

  public clientValueSelected(clientId: string) {
    this.setClient(clientId);
  }
  public csrChanged(val) {
    const original = this.currentTask != null ? this.currentTask.AssigneeAgileId : '';

    // if this is a new task added from the UI, restrict the selection to 'Open'
    if (!original && this.t.Status.value == TaskStatuses.Open) {
      return;
    }

    // // if an existing task's CSR is changed,
    // // move status to Reassigned and disable
    // if (original && val.target.value !== original) {
    //   this.t.Status.setValue(TaskStatuses.Reassigned);
    //   this.t.Status.disable();
    // } else {
    //   this.t.Status.enable();
    // }
  }

  public editTask(content, task: Task) {
    this.t.Status.enable();
    this.invalidSelClient = false;
    // set required fields
    this.taskForm.controls.DueDate.setValidators(Validators.required);
    this.taskForm.controls.AssigneeAgileId.setValidators(Validators.required);
    this.taskForm.controls.Status.setValidators(Validators.required);

    this.taskService.setLastTicketView(task.Id).subscribe(() => {});
    task.IsRead = true;

    this.currentTask = task;

    // NOTE: temporarily disable rich text editor logic
    // this.currentTask.Description = this.currentTask.Description?.replace(/\n/g, '<br />') || '';
    // this.currentTask.TaskNote = this.currentTask.TaskNote?.replace(/\n/g, '<br />') || '';

    // NOTE: temporary code to handle existing rich text entries
    const whiteSpace = /(&nbsp;|<br\s?\/>)/g;
    const htmlTags = /<[^>]*>?/gm;
    this.currentTask.Description = this.currentTask.Description?.replace(whiteSpace, '\r\n') || '';
    this.currentTask.Description = this.currentTask.Description?.replace(htmlTags, '') || '';
    this.currentTask.TaskNote = this.currentTask.TaskNote?.replace(whiteSpace, '\r\n') || '';
    this.currentTask.TaskNote = this.currentTask.TaskNote?.replace(htmlTags, '') || '';

    const d = moment(task.DueDate);
    this.dueByTime = {
      hour: parseInt(d.format('HH'), 10),
      minute: parseInt(d.format('mm'), 10),
      second: parseInt(d.format('ss'), 10)
    };

    task.AssigneeAgileId = task.AssigneeAgileId.toLowerCase();

    this.taskForm.patchValue(task);
    this.t.DueDate.setValue(formatDatePickerValue(task.DueDate));

    this.taskMinDate = {
      ...formatDatePickerValue(task.CreatedOn),
      hour: new Date(task.CreatedOn).getHours(),
      minute: new Date(task.CreatedOn).getMinutes()
    };

    this.invalidTaskDueDate = false;
    this.t.DueDate.updateValueAndValidity();

    this.t.Type.setValue(task.Type);

    if (task.ClientCode && task.ClientName) {
      this.selClientCode = task.ClientCode;
      this.selClientName = task.ClientName;
      this.setClient(task.ClientCode);
      this.initialClientQuery = `${task.ClientCode} - ${task.ClientName}`;
    } else {
      this.initialClientQuery = '';
    }

    if (task.ClientCode) {
      this._fetchActiveClientNote(task.ClientCode);
    }

    this.onNotesNavChange(null);
    this.taskStatusChanged();
    this.ngModalService
      .open(content, {
        size: 'lg',
        backdrop: 'static',
        keyboard: false,
        windowClass: 'centered'
      })
      .result.then(
        result => {
          // success
          const status = task.Status;
          const oldTask = { ...task };
          task.ClientCode = this.selClientCode;
          task.ClientName = this.selClientName;
          task.Description = this.t.Description.value;
          task.Name = this.authService.getCurrentUserName();
          task.Status = this.t.Status.value;
          task.Priority = this.t.Priority.value;
          task.Type = this.t.Type.value;
          task.AssigneeAgileId = this.t.AssigneeAgileId.value;

          // task.Subject is not assigned here intentionally.  We don't want users overwriting
          // the generated subject as it is used for administrating tickets in Zoho
          // bug 35387 - bbarrett 2024-10-08

          if (task.TaskNote != this.t.TaskNote.value) {
            task.TaskNote = this.t.TaskNote.value;
            task.TaskNoteLastEditedUserName = this.currentUserContactId;
          }

          // concatenate date and time
          const date = moment(parseDatePickerValue(this.t.DueDate.value));
          date.set('hour', this.dueByTime ? this.dueByTime.hour : 0);
          date.set('minute', this.dueByTime ? this.dueByTime.minute : 0);
          task.DueDate = date.toDate();
          if (task.AssigneeAgileId != oldTask.AssigneeAgileId) {
            this._addReassignedMessageToTask(task);
          }
          this.taskService.updateTask(task).subscribe(id => {
            this.toastService.show('Save successful');

            this._wasTaskResolved(oldTask.Status, task);

            // audit
            const deltas = getObjectDeltas(oldTask, task, ['IntegrityHash']);
            this.auditService.auditTask(
              `apollo-task-updated`,
              {
                Id: task.Id,
                ClientCode: task.ClientCode
              },
              {
                ...deltas
              }
            );

            // set another last task view so this user sees these changes as read
            this.taskService.setLastTicketView(id).subscribe(() => {
              // refresh task lists and counts
              if (oldTask.Type != task.Type) {
                // if both old task and new task are payroll, do nothing
                if (oldTask.Type.includes('Payroll') && task.Type.includes('Payroll')) {
                  return;
                  // if neither old task nor new task are payroll, do nothing
                } else if (!oldTask.Type.includes('Payroll') && !task.Type.includes('Payroll')) {
                  return;
                } else {
                  // update both lists and the chart
                  this._removeFromTaskList(oldTask);
                  this._addToTaskList(task);
                }
              }

              this._removeFromTaskCount(oldTask);
              this._addToTaskCount(task);
              this._refreshTaskChart();
            });
          });

          this.taskForm.reset();
        },
        () => {
          // dismiss
          this.taskForm.reset();
        }
      );
  }

  // refresh client notes button action event
  public getActiveClientNote() {
    if (!this.selClientCode) {
      return null;
    } else {
      this._fetchActiveClientNote(this.selClientCode, true);
    }
  }

  // called on inital page load and whenever
  // both task lists are refreshed.
  // always revert back to page one here
  // and scroll back to the top of the table
  public getAllTasks() {
    // scroll back to the top of the table
    if (this.taskContainer) {
      this.taskContainer.nativeElement.scrollTop = 0;
    }

    this.otherTaskSearchConfig.PageNumber = 1;
    this.payrollTaskSearchConfig.PageNumber = 1;

    const obs: Observable<PagedPayload<Task>>[] = [];
    obs.push(
      this.taskService
        .getNonPayrollTasks(this.otherTaskSearchConfig)
        .pipe(
          tap(otherTasks => this._updateOtherTasks(otherTasks as PagedPayload<Task>, false, true))
        )
    );
    obs.push(
      this.taskService
        .getPayrollTasks(this.payrollTaskSearchConfig)
        .pipe(
          tap(payrollTasks =>
            this._updatePayrollTasks(payrollTasks as PagedPayload<Task>, false, true)
          )
        )
    );

    forkJoin(obs).subscribe(results => {
      if (!this.hasWarnedAboutOldTasks) {
        this.hasWarnedAboutOldTasks = true;
        this.checkPastDueTasks();
      }
    });
  }

  public getFirstName() {
    const user = this.authService.user;
    if (user && user.GivenName) {
      return user.GivenName;
    }
    return '';
  }

  public getLastName() {
    const user = this.authService.user;
    if (user && user.FamilyName) {
      return user.FamilyName;
    }
    return '';
  }

  // called from child component on infinite scroll triggers.
  public getOtherTasks(searchConfig: TaskSearchConfig, fetchCount = false) {
    // if next page number is passed, append to current results
    let append = searchConfig.PageNumber > this.payrollTaskSearchConfig.PageNumber;

    this.otherTaskSearchConfig = searchConfig;
    this.taskService.getNonPayrollTasks(searchConfig).subscribe(response => {
      this._updateOtherTasks(response, append, fetchCount);
    });
  }

  // called from child component on infinite scroll triggers.
  public getPayrollTasks(searchConfig: PayrollTaskSearchConfig, fetchCount = false) {
    // if next page number is passed, append to current results
    let append = searchConfig.PageNumber > this.payrollTaskSearchConfig.PageNumber;

    this.payrollTaskSearchConfig = searchConfig;
    this.taskService.getPayrollTasks(searchConfig).subscribe(response => {
      this._updatePayrollTasks(response, append, fetchCount);
    });
  }

  public isCurrentTaskRestrictedEdit() {
    if (this.currentTask?.CreatorAgileId == null) {
      return false;
    }

    var apiCreators = this.appConfigService.getConfig().TaskAPICreators.map(s => s.toLowerCase());
    return apiCreators.includes(this.currentTask.CreatorAgileId.toLowerCase());
  }

  public onChartInit(ec) {
    this.echartsInstance = ec;
  }

  public onNotesNavChange(evnt) {
    if (!this.currentTask) {
      this.taskNoteLastUpdateBy = this.taskNoteLastUpdateDate = this.taskNoteLastUpdateTime = null;
      return;
    }

    if (!evnt || (evnt && evnt.nextId == 'task-notes')) {
      // viewing task notes
      const lastEditBy = this.teamMembers.find(
        x => x.userName == this.currentTask.TaskNoteLastEditedUserName
      );
      const lastEditOn = this.currentTask.TaskNoteLastEditedOn;
      this.taskNoteLastUpdateBy = lastEditBy ? lastEditBy.userName : '';
      this.taskNoteLastUpdateDate = lastEditOn ? new Date(lastEditOn).toLocaleDateString() : '';
      this.taskNoteLastUpdateTime = lastEditOn ? new Date(lastEditOn).toLocaleTimeString() : '';
    } else if (evnt && evnt.nextId == 'client-notes' && this.selClientNote) {
      // viewing client notes
      this._setClientNoteBlameData();
    }
  }

  public onTaskTabChange(evnt) {
    if (!evnt || (evnt && evnt.nextId == 'payroll-tasks')) {
      this.currentTaskList = TaskListMode.Payroll;
    } else {
      this.currentTaskList = TaskListMode.Other;
    }

    this._refreshTaskChart();

    // when user changes tabs, clear selected tasks.
    // if selected tasks need to be persisted between tab changes
    // separate selTask collections will need to be maintained
    this.selTasks = {};
    this.clearSelTasksSubject.next();
    this.expandedTasks = {};
  }

  public onTaskTypeChange() {
    const type = this.taskForm.get('Type').value;
  }

  // save logic for permanent client note on task
  public saveClientNote() {
    // only save notes if a client was added to the task
    // or the note was truly changed
    if (!this.selClientCode || !this.t.ClientNote.dirty) {
      return;
    }

    // check if user changed client notes
    if (!this.selClientNote && this.t.ClientNote.value) {
      // client didn't start with a note but now has one
      const note = new ClientNote();
      const u = this.authService.getCurrentUserName();
      note.Note = this.t.ClientNote.value ? this.t.ClientNote.value : '';
      note.CreatedBy = note.LastModifiedBy = u;
      this.companyService
        .createClientNote(this.selClientCode, note)
        .pipe(
          catchError(err => {
            this._handleClientNoteRejection(err);
            return of(null);
          })
        )
        .subscribe(n => {
          if (!n) {
            return;
          }

          this.selClientNote = n;
          this._setClientNoteBlameData();
          this.toastService.show('Save successful');
        });
    } else if (this.selClientNote && this.selClientNote.Note !== this.t.ClientNote.value) {
      // client note existed and no longer matches
      this.selClientNote.Note = this.t.ClientNote.value ? this.t.ClientNote.value : '';
      this.selClientNote.LastModifiedBy = this.authService.getCurrentUserName();

      if (this.selClientNote.SourcedFromAgile) {
        // note was sourced from agile, insert changes as new SDW note
        this.selClientNote.CreatedBy = this.authService.getCurrentUserName();
        this.companyService
          .createClientNote(this.selClientCode, this.selClientNote)
          .pipe(
            catchError(err => {
              this._handleClientNoteRejection(err);
              return of(null);
            })
          )
          .subscribe(n => {});
      } else {
        this.companyService
          .updateClientNote(this.selClientCode, this.selClientNote)
          .pipe(
            catchError(err => {
              this._handleClientNoteRejection(err);
              return of(null);
            })
          )
          .subscribe((n: ApiResponse<ClientNote>) => {
            if (!n) {
              return;
            }

            this.selClientNote = n.Model;
            this._setClientNoteBlameData();
            this.toastService.show('Save successful');
          });
      }
    }
  }

  public searchCSRs(searchQuery: string) {
    // bug 7385 - Supervisors and CSRs will have access to all CSRs in any office
    this.csrSearchResults = [];
    this.teamMembers.forEach(c => {
      if (
        `${c.firstName.toLowerCase()} ${c.lastName.toLowerCase()}`.indexOf(
          searchQuery.toLowerCase()
        ) > -1
      ) {
        this.csrSearchResults.push(c);
      }
    });
  }

  public setClient(clientId: string) {
    this.selClientCode = clientId;
    const selClient = this.clients.find(x => x.Id == clientId);
    this._fetchActiveClientNote(clientId);

    if (selClient) {
      this.selClientName = selClient.DBA || selClient.Name;
      const text = `${this.selClientCode} - ${this.selClientName}`;
      this.t.ClientCode.setValue(text);
    } else {
      this.companyService.getCompany(clientId).subscribe(resp => {
        const text = `${resp.Id} - ${resp.DBA || resp.Name}`;
        this.t.ClientCode.setValue(text);
      });
    }
  }

  public setClientData(data: PagedPayload<Client>) {
    if (data && data.Items && data.Items.length > 0) {
      this.clients = data.Items;
    } else {
      this.clients = [];
    }
  }

  public setClientQuery(q: string) {
    if (q !== this.selClientCode + ' - ' + this.selClientName) {
      this.selClientCode = '';
      this.selClientName = '';

      this.invalidSelClient = q.trim() !== '';

      this.t.ClientNote.setValue('');
    } else {
      this.invalidSelClient = false;
    }

    this.clientSearchObs = this.companyService.searchCompanies({ query: q });
  }

  public showBatchEditBtn() {
    if (Object.keys(this.selTasks).filter(x => this.selTasks[x]).length) {
      return true;
    } else {
      return false;
    }
  }

  // assign element ref object so it can
  // be scrolled to the top when changing views
  public setTaskContainer(taskContainer: ElementRef) {
    this.taskContainer = taskContainer;
  }

  public taskStatusChanged() {
    // if status is Resolved or Closed
    // due by date/time cannot be edited
    if (
      (this.currentTask || Object.keys(this.selTasks).length > 0) &&
      this.t.Status.value &&
      (this.t.Status.value == TaskStatuses.Resolved || this.t.Status.value == TaskStatuses.Closed)
    ) {
      this.t.DueDate.clearValidators();
      this.disableTaskDueDate = true;
      this.invalidTaskDueDate = false;

      if (this.currentTask) {
        // revert back to the originally set due date/time
        const d = moment(this.currentTask.DueDate);
        this.dueByTime = {
          hour: parseInt(d.format('HH'), 10),
          minute: parseInt(d.format('mm'), 10),
          second: parseInt(d.format('ss'), 10)
        };

        this.t.DueDate.setValue(formatDatePickerValue(this.currentTask.DueDate));
      } else if (Object.keys(this.selTasks).length > 0) {
        this.dueByTime = null;
        this.t.DueDate.setValue('');
      }

      this.t.DueDate.updateValueAndValidity();
    } else {
      this.disableTaskDueDate = false;
      this.t.DueDate.setValidators([Validators.required]);
    }

    this.t.DueDate.updateValueAndValidity();
  }

  public toggleExpandedTasks(taskId: number): void {
    if (this.expandedTasks[taskId]) {
      this.expandedTasks[taskId] = null;
    } else {
      this.taskService.getSubTasks(taskId).subscribe(subTasks => {
        this.expandedTasks[taskId] = {};
        const before = subTasks.filter(x => x.TaskGroupId == CompanySubTaskGroup.BeforePayroll);
        const after = subTasks.filter(x => x.TaskGroupId == CompanySubTaskGroup.AfterPayroll);

        if (before.length) {
          this.expandedTasks[taskId][CompanySubTaskGroup.BeforePayroll] = before.sort(
            (a, b) => a.Position - b.Position
          );
        }

        if (after.length) {
          this.expandedTasks[taskId][CompanySubTaskGroup.AfterPayroll] = after.sort(
            (a, b) => a.Position - b.Position
          );
        }
      });
    }
  }

  public toggleSubTaskCompletion(model: SubTaskCompletion) {
    if (model.IsComplete) {
      this.taskService.markSubTaskAsIncomplete(model.TaskId, model.SubTaskId).subscribe(() => {
        this.toastService.show('Save successful');
      });
    } else {
      this.taskService.markSubTaskAsComplete(model.TaskId, model.SubTaskId).subscribe(() => {
        this.toastService.show('Save successful');
      });
    }
  }

  public updateSelectedTasks(selTasks) {
    this.selTasks = selTasks;
  }

  // #endregion

  // #region private methods
  private _addReassignedMessageToTask(task: Task) {
    const dateFormatted = new Date().toLocaleDateString();
    const timeFormatted = new Date().toLocaleTimeString(undefined, { timeZoneName: 'short' });
    task.Description = `${
      task.Description
    }<br /><br />This ticket was reassigned to you by ${this.getFirstName()} ${this.getLastName()} on ${dateFormatted} at ${timeFormatted}.`;
  }
  private _addToTaskCount(task: Task) {
    const countType = task.Type.includes('Payroll') ? 'payroll' : 'other';

    // if the tasks due date is greater than the end of today,
    // do not include in count
    if (this._isTaskFutureDated(task)) {
      return;
    }

    if (task.Status == TaskStatuses.Open) {
      this.taskCount[countType].open++;
    } else if (task.Status == TaskStatuses.Closed) {
      this.taskCount[countType].closed++;
    } else {
      this.taskCount[countType].remaining++;
    }
  }

  private _addToTaskList(task: Task) {
    if (task.Type.includes('Payroll')) {
      this.payrollTasks.push(task);
    } else {
      this.otherTasks.push(task);
    }
  }

  private _batchUpdateTasks() {
    // push all update tasks calls into a forkJoin
    // so we know when they have all completed
    const obs = [];
    // keep record of current state for auditing
    const oldTasks = {};
    Object.keys(this.selTasks).forEach(key => {
      if (!this.selTasks[key]) {
        return;
      }

      // fetch task object
      const task = this.selTasks[key];
      oldTasks[key] = { ...task };

      if (this.t.DueDate.value && !this.disableTaskDueDate) {
        // concatenate date and time
        const date = moment(parseDatePickerValue(this.t.DueDate.value));
        date.set('hour', this.dueByTime ? this.dueByTime.hour : 0);
        date.set('minute', this.dueByTime ? this.dueByTime.minute : 0);
        task.DueDate = date.toDate();
      }

      if (this.t.Status.value) {
        task.Status = this.t.Status.value;
      }

      if (this.t.AssigneeAgileId.value) {
        task.AssigneeAgileId = this.t.AssigneeAgileId.value;
        this._addReassignedMessageToTask(task);
      }

      // push each update task request into an observable
      // using rxjs pipe/tap to manipulate the update task response model
      obs.push(
        this.taskService.updateTask(task).pipe(
          tap(resp => {
            this._wasTaskResolved(oldTasks[resp].Status, task);

            // audit
            const deltas = getObjectDeltas(oldTasks[resp], task, ['IntegrityHash']);
            this.auditService.auditTask(
              `apollo-task-updated`,
              {
                ClientCode: task.clientCode,
                Id: task.Id
              },
              {
                ...deltas
              }
            );

            this.taskService.setLastTicketView(resp).subscribe(() => {});
          })
        )
      );
    });

    if (!obs.length) {
      return;
    }

    forkJoin<any>(obs).subscribe(() => {
      // fetch all tasks again and refresh everything
      this.getAllTasks();

      // refresh view map to toggle read/unread
      this.taskService.getLastViewed().subscribe(map => {
        this.taskLastViewedMap = map;

        this._refreshTaskReadStatus();
        this.selTasks = {};
        this.clearSelTasksSubject.next();
      });

      this.toastService.show('Save successful');
    });
  }

  private _fetchActiveClientNote(clientCode: string, setBlame = false) {
    this.companyService.getActiveClientNote(clientCode).subscribe(resp => {
      this.selClientNote = resp;
      this.t.ClientNote.setValue(resp ? resp.Note : '');

      // only set for certain callers depending
      // on what notes tab the user is on
      if (setBlame) {
        this._setClientNoteBlameData();
      }
    });
  }

  private _handleClientNoteRejection(err: any) {
    // lastEditedOn needs to be passed back to the client
    // in order to render the proper timezone
    if (err && err.Message) {
      let msg = err.Message;
      if (err.Dates) {
        msg += ` on ${new Date(err.Dates['LastModifiedOn']).toLocaleString()}`;
      }

      msg += '. Please copy your changes to your clipboard and refresh the note to continue.';
      this.modalService.showAlertModal(msg, ModalStates.Error);
    }
  }

  // determine if tasks due date is greater than the end of today
  private _isTaskFutureDated(task: Task) {
    return new Date(task.DueDate).getTime() > new Date().setHours(23, 59, 59, 999);
  }

  private _refreshOtherTaskCounts() {
    // get count of total open/closed other tasks.
    // this count does not include any filter information
    // it should only be driven off of the task view
    // which populates the assignee config
    const config = new TaskSearchConfig();
    config.Assignees = this.otherTaskSearchConfig.Assignees;

    // bug 9045. task counts are limited to those with
    // a due date of today or prior.
    config.DueDate = new Date(new Date().setHours(23, 59, 59, 999));

    this.taskService.getNonPayrollTaskCount(config).subscribe(counts => {
      this.taskCount.other.open = counts[TaskStatuses[TaskStatuses.Open]] || 0;
      this.taskCount.other.closed = counts[TaskStatuses[TaskStatuses.Closed]] || 0;
      this.taskCount.other.remaining = 0;
      Object.keys(counts)
        .filter(
          status =>
            status != TaskStatuses[TaskStatuses.Open] && status != TaskStatuses[TaskStatuses.Closed]
        )
        .forEach(key => {
          this.taskCount.other.remaining += counts[key];
        });

      if (this.currentTaskList == TaskListMode.Other) {
        this._refreshTaskChart();
      }
    });
  }

  private _refreshPayrollTaskCounts() {
    // get count of total open/closed payroll tasks
    // this count does not include any filter information
    // it should only be driven off of the task view
    // which populates the assignee config
    const config = new PayrollTaskSearchConfig();
    config.Assignees = this.payrollTaskSearchConfig.Assignees;

    // bug 9045. task counts are limited to those with
    // a due date of today or prior.
    config.DueDate = new Date(new Date().setHours(23, 59, 59, 999));

    this.taskService.getPayrollTaskCount(config).subscribe(counts => {
      this.taskCount.payroll.open = counts[TaskStatuses[TaskStatuses.Open]] || 0;
      this.taskCount.payroll.closed = counts[TaskStatuses[TaskStatuses.Closed]] || 0;

      this.taskCount.payroll.remaining = 0;
      Object.keys(counts)
        .filter(
          status =>
            status != TaskStatuses[TaskStatuses.Open] && status != TaskStatuses[TaskStatuses.Closed]
        )
        .forEach(key => {
          this.taskCount.payroll.remaining += counts[key];
        });

      if (this.currentTaskList == TaskListMode.Payroll) {
        this._refreshTaskChart();
      }
    });
  }

  private _refreshTaskChart() {
    let count =
      this.currentTaskList == TaskListMode.Payroll ? this.taskCount.payroll : this.taskCount.other;

    this.options.title.text = `${count.closed} Closed`;
    this.options.series[0].data[0] = { value: count.closed };
    this.options.series[0].data[1] = { value: count.open + count.remaining };

    this.echartsInstance?.setOption(this.options);
  }

  private _refreshTaskReadStatus() {
    let tasks = [];

    if (this.currentTaskList == TaskListMode.Payroll) {
      tasks = this.payrollTasks;
    } else if (this.currentTaskList == TaskListMode.Other) {
      tasks = this.otherTasks;
    }

    if (!this.taskLastViewedMap || !tasks.length) {
      return;
    }

    // loop through last view map to set task.IsRead
    tasks.forEach(task => {
      const lastViewed = this.taskLastViewedMap[task.Id];
      if (lastViewed && moment(task.LastEditedOn).isSameOrBefore(moment(lastViewed))) {
        // the user viewed this task AFTER its last update
        task.IsRead = true;
      }
    });
  }

  private _removeFromTaskCount(task) {
    const countType = task.Type.includes('Payroll') ? 'payroll' : 'other';

    if (this._isTaskFutureDated(task)) {
      return;
    }

    if (task.Status == TaskStatuses.Open && this.taskCount[countType].open > 0) {
      this.taskCount[countType].open--;
    } else if (task.Status == TaskStatuses.Closed && this.taskCount[countType].closed > 0) {
      this.taskCount[countType].closed--;
    } else if (this.taskCount[countType].remaining > 0) {
      this.taskCount[countType].remaining--;
    }
  }

  private _removeFromTaskList(task: Task) {
    if (task.Type.includes('Payroll')) {
      this.payrollTasks = this.payrollTasks.filter(t => t.Id != task.Id);
    } else {
      this.otherTasks = this.otherTasks.filter(t => t.Id != task.Id);
    }
  }

  private _setClientNoteBlameData() {
    if (!this.selClientNote) {
      return;
    }

    this.taskNoteLastUpdateBy = this.selClientNote.LastModifiedBy;
    this.taskNoteLastUpdateDate = new Date(this.selClientNote.LastModifiedOn).toLocaleDateString();
    this.taskNoteLastUpdateTime = new Date(this.selClientNote.LastModifiedOn).toLocaleTimeString();
  }

  private _setupTasks() {
    if (this.aclService.HasMinimumRoleByName(this.auth.getCurrentUserId(), 'CSR1')) {
      // use 'userName' as key to pass to server, which derefs into freshdesk Id
      this.currentUserContactId = this.authService.getCurrentUserName();
      this.taskView = TaskViewMode.MyTasks;
      this.currentTaskList = TaskListMode.Payroll;

      this.adminService.getOfficeCSRMap().subscribe(resp => {
        this.csrOfficeMap = resp;
        // bug 7385 - Supervisors AND CSRs can view tasks and reassign to a CSR in ANY office
        this.teamMembers = [];

        this.csrOfficeMap.forEach(office => {
          office.CSRs.forEach(csr => {
            this.teamMembers.push(csr);
          });
        });

        // get tasks and set last viewed
        this.taskService.getLastViewed().subscribe(map => {
          this.taskLastViewedMap = map;

          // set task view to My Task, which will fetch tasks
          this.changeTaskView(TaskViewMode.MyTasks);
          // set isRead
          this._refreshTaskReadStatus();
        });
      });

      this.taskService.getTaskTypes().subscribe(types => {
        this.taskTypes = types;
      });
    } else {
      this.currentTaskList = TaskListMode.Other;
    }
  }

  private _updateOtherTasks(response: PagedPayload<Task>, append: boolean, fetchCount: boolean) {
    if (append) {
      // infinite scroll - append to current list
      this.otherTasks = this.otherTasks.concat(response.Items);
    } else {
      this.otherTasks = response.Items;
    }

    // set isRead property on Other Tasks
    this._refreshTaskReadStatus();

    this.otherTaskSearchConfig.PageNumber = response.Pagination.Page;
    this.otherTaskSearchConfig.PageSize = response.Pagination.PerPage;
    this.otherTaskSearchConfig.TotalPages = response.Pagination.Pages;

    if (fetchCount) {
      this._refreshOtherTaskCounts();
    }
  }

  private _updatePayrollTasks(response: PagedPayload<Task>, append: boolean, fetchCount: boolean) {
    if (append) {
      // infinite scroll - append to current list
      this.payrollTasks = this.payrollTasks.concat(response.Items);
    } else {
      this.payrollTasks = response.Items;
    }

    // We can get duplicate tasks from the API here.  They are primarily caused by off-cycle payrolls having
    // more than one PRI schedule record per payroll. -bbarrett 2023-10-27
    this.payrollTasks = DistinctBy(this.payrollTasks, p => p.Id);

    // set isRead property on Payroll Tasks
    this._refreshTaskReadStatus();

    this.payrollTaskSearchConfig.PageNumber = response.Pagination.Page;
    this.payrollTaskSearchConfig.PageSize = response.Pagination.PerPage;
    this.payrollTaskSearchConfig.TotalPages = response.Pagination.Pages;

    if (fetchCount) {
      this._refreshPayrollTaskCounts();
    }
  }

  private _wasTaskResolved(oldTaskStatus: TaskStatuses, newTask: Task) {
    if (
      newTask.Status == TaskStatuses.Resolved &&
      oldTaskStatus != TaskStatuses.Resolved &&
      oldTaskStatus != TaskStatuses.Closed
    ) {
      if (!newTask.AssigneeAgileId || !newTask.CreatorAgileId) {
        this.auditService.auditTask(`apollo-task-notification-failure`, newTask);
      } else {
        const creatorNotification = new UserNotification();

        creatorNotification.UserId = newTask.CreatorAgileId;
        creatorNotification.TimeCreated = new Date();
        creatorNotification.Subject = newTask.Subject + ' Task Resolved';
        creatorNotification.Body = newTask.Description;
        creatorNotification.Active = true;
        creatorNotification.IsRead = false;
        creatorNotification.IsDismissed = false;
        this.notificationService.create(creatorNotification).subscribe(() => {
          // raise event to the nav bar to refresh notifications
          window.dispatchEvent(new CustomEvent('refreshUserNotifications'));
        });

        if (newTask.AssigneeAgileId !== newTask.CreatorAgileId) {
          // send a notification to the assignee
          const assigneeNotification = { ...creatorNotification };
          assigneeNotification.UserId = newTask.AssigneeAgileId;

          this.notificationService.create(assigneeNotification).subscribe(() => {
            // raise event to the nav bar to refresh notifications
            window.dispatchEvent(new CustomEvent('refreshUserNotifications'));
          });
        }
      }
    }
  }

  // #endregion
}
